'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import {
  fetchCountries,
  fetchGenders,
  uploadFile,
} from '@eliadmin/core/src/api/general/general-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useRouter } from 'next/navigation';
import { useMemo, useRef, useState } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';

type ProfileEditFormValues = z.infer<typeof profileEditSchema>;
export function ProfileEditForm() {
  const customer = useCustomerStore(s => s.me!.customer);
  const authToken = useCustomerStore(s => s.me!.authToken);

  const ref = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | undefined>(undefined);

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const { data: genders = { data: [] }, isLoading: isGendersLoading } = useQuery({
    queryKey: [CacheKey.GENDERS, locale],
    queryFn: () => fetchGenders({ locale }),
    refetchOnWindowFocus: false,
  });

  const form = useForm<ProfileEditFormValues>({
    resolver: zodResolver(profileEditSchema),
    defaultValues: {
      name: customer?.name ?? '',
      surname: customer?.surname ?? '',

      company_buy: customer?.company_buy ?? false,
      company: customer?.company ?? '',
      ico: customer?.ico ?? '',
      dic: customer?.dic ?? '',
      ic_dph: customer?.ic_dph ?? '',

      phone: customer?.phone ?? '',
      dial: `${customer.phone?.split(' ')[0] ?? ''}`,

      country: customer?.country ?? undefined,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: updateCustomer,
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await uploadFile({
          authToken,
          formData,
          locale,
        });
        data.image = res.data;
      } catch (e) {
        //TODO: handle error / create custom field for this error
        return;
      }
    }

    if (!data.dial || !data.phone) {
      form.setError('phone', { type: 'manual', message: t('text1386') });
      return;
    }

    if (data?.phone?.replace(data.dial, '').trim().startsWith('+') || data.dial.length > 5) {
      form.setError('phone', { type: 'manual', message: t('text1387') });
      return;
    }

    const transformedData = {
      name: data.name,
      surname: data.surname,
      company_buy: data.company_buy,
      company: data.company,
      ico: data.ico,
      dic: data.dic,
      ic_dph: data.ic_dph,

      phone_preset: data.dial,
      phone: data.phone.replace(data.dial, '').trim(),
      country: {
        id: data.country?.id || customer.country,
      },
      image: data.image,
    };

    try {
      await mutateAsync({
        locale,
        data: transformedData as any,
        authToken,
      });

      router.refresh();
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }
    }
  });

  const handlePickFile = () => {
    ref.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      setFile(undefined);
      return;
    }

    setFile(file);
  };

  const { t } = useTranslations();

  const countryId = form.watch('country.id');

  return (
    <Form {...form}>
      <form className="grid grid-cols-12 gap-4" onSubmit={handleSubmit}>
        <div className={cn('col-span-12 flex flex-col  gap-4 py-5 md:flex-row')}>
          <Image
            src={
              file ? URL.createObjectURL(file) : (customer.image?.url ?? '/images/placeholder.png')
            }
            alt="Avatar"
            width="0"
            height="0"
            sizes="100vw"
            className={cn(
              'h-auto w-[187px] rounded-lg object-contain',
              'ea-profile-edit__user-image',
            )}
          />
          <div className={cn('flex flex-col items-start justify-start gap-4')}>
            <div className={cn('flex flex-col items-start  gap-1')}>
              <h5 className={cn('text-2xl font-bold')}>
                {customer.name} {customer.surname}
              </h5>
              <p className={cn('text-sm text-muted-foreground')}>{customer.email}</p>
            </div>
            <input type="file" ref={ref} className={cn('hidden')} onChange={handleFileChange} />
            <Button
              type="button"
              className="w-full md:w-auto"
              variant="outline"
              onClick={handlePickFile}
            >
              {t('text250')}
            </Button>
          </div>
        </div>

        {/* personal data */}
        <SectionHeader title={t('text198')} />
        <Field
          required
          control={form.control}
          name="name"
          label={t('text33')}
          placeholder={t('text33')}
        />
        <Field
          required
          control={form.control}
          name="surname"
          label={t('text34')}
          placeholder={t('text34')}
        />
        {/* <Field
          required
          control={form.control}
          name="email"
          type="email"
          label={t('text35')}
          placeholder={t('text35')}
        /> */}
        <div className="col-span-12 md:col-span-6">
          <Field
            control={form.control}
            type="tel"
            name="phone"
            label={t('text36')}
            placeholder={t('text36')}
            isDisabled={isCountriesLoading}
            options={countries.data.map(c => ({
              value: c.id,
              label: c.name,
            }))}
          />
        </div>
        <Field
          control={form.control}
          type="select"
          name="country.id"
          label={t('text519')}
          placeholder={t('text519')}
          isDisabled={isCountriesLoading}
          options={countries.data.map(c => ({
            value: c.id,
            label: c.name,
          }))}
        />

        <SectionHeader title={t('text689')} />
        <div className="col-span-12 md:col-span-12">
          <Field control={form.control} type="checkbox" name="company_buy" label={t('text361')} />
        </div>
        <div className="col-span-12 md:col-span-6">
          <Field
            control={form.control}
            name="company"
            label={t('text37')}
            placeholder={t('text37')}
            required
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <Field
            control={form.control}
            name="ico"
            label={t('text42')}
            placeholder={t('text42')}
            required
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <Field
            control={form.control}
            name="dic"
            label={t('text43')}
            placeholder={t('text43')}
            required
          />
        </div>
        {countryId === 'SK' && (
          <div className="col-span-12 md:col-span-6">
            <Field
              control={form.control}
              name="ic_dph"
              label={t('text44')}
              placeholder={t('text44')}
            />
          </div>
        )}

        {/* address */}
        {/* <Field
          control={form.control}
          name="address"
          label={t('text38')}
          placeholder={t('text38')}
        />
        <Field
          control={form.control}
          name="street_number"
          label={t('text438')}
          placeholder={t('text438')}
        />
        <Field control={form.control} name="city" label={t('text39')} placeholder={t('text39')} />
        <Field
          control={form.control}
          name="postcode"
          label={t('text40')}
          placeholder={t('text40')}
        /> */}
        {/* TODO: countries */}

        {/* billing */}
        {/*<SectionHeader title={t('text293')} />
        <Field control={form.control} name="iban" label={t('text294')} placeholder={t('text294')} />
        <Field
          control={form.control}
          name="swift"
          label={t('text295')}
          placeholder={t('text295')}
        />*/}

        <div className="col-span-12 flex flex-row justify-end">
          <Button
            type="submit"
            className="w-full md:w-auto"
            isLoading={form.formState.isSubmitting}
          >
            {t('text201')}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <>
      <div className="col-span-12 pt-4">
        <Separator />
      </div>
      <div className="col-span-12">
        <h4 className="text-xl font-semibold text-foreground">{title}</h4>
      </div>
    </>
  );
}

type FieldProps = {
  control: Control<ProfileEditFormValues>;
  name: FieldPath<ProfileEditFormValues>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'] | 'tel' | 'radio';
  options?: { value: string | number; label: string }[];
  isFullWidth?: boolean;
  isDisabled?: boolean;
};

function Field(props: FieldProps) {
  const form = useFormContext();
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required}>
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <Select
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={props.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {props.options?.map(o => (
                        <SelectItem key={o.value} value={String(o.value)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'radio') {
      if (!props.options) {
        throw new Error('Radio type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="flex flex-col gap-2">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                    className="flex flex-row gap-4 pl-2"
                  >
                    {props.options?.map(o => (
                      <div key={o.value} className="flex gap-2">
                        <RadioGroupItem id={`radiogroupitem-${o.value}`} value={String(o.value)} />
                        <Label className="flex items-center" htmlFor={`radiogroupitem-${o.value}`}>
                          <p className="text-sm font-semibold text-foreground">{o.label}</p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'tel') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <FormLabel>{props.label}</FormLabel>
              <FormControl>
                <PhoneInputField
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  onChange={(val, meta) => {
                    field.onChange(meta.inputValue);
                    form.setValue('dial', `+${meta.country.dialCode}`);
                  }}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                  inputClassName={cn('bg-input')}
                  inputStyle={{
                    backgroundColor: 'hsl(var(--input)) !important',
                    height: '48px !important',
                    paddingLeft: '20px',
                    width: '100%',
                    borderEndEndRadius: 'var(--radius)',
                    borderStartEndRadius: 'var(--radius)',
                    border: '1px solid #cccccc',
                  }}
                  countrySelectorStyleProps={{
                    buttonClassName: 'bg-input',
                    buttonStyle: {
                      height: '48px !important',
                      backgroundColor: 'hsl(var(--input)) !important',
                      borderStartStartRadius: 'var(--radius)',
                      borderEndStartRadius: 'var(--radius)',
                      paddingLeft: '12px',
                      border: '1px solid #cccccc',
                    },
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }
    if (props.type === 'checkbox') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <FormControl>
                <div className="flex w-full flex-row items-center gap-2">
                  <Checkbox
                    {...(field as any)}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                  />
                  {props.label && <FormLabel>{props.label}</FormLabel>}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required}>
            <FormLabel>{props.label}</FormLabel>
            <FormControl>
              <Input
                placeholder={props.placeholder}
                type={props.type}
                {...(field as any)}
                disabled={props.isDisabled}
                aria-disabled={props.isDisabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.control,
    props.isDisabled,
    props.required,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.type,
  ]);

  return (
    <div
      className={cn('col-span-12 md:col-span-6', {
        ['md:col-span-12']: props.isFullWidth,
      })}
    >
      {content}
    </div>
  );
}

export const profileEditSchema = z.object({
  name: z.string(),
  dial: z.string().optional(),
  surname: z.string(),
  phone: z.string().optional(),
  country: z
    .object({
      id: z.string().or(z.number()).optional(),
    })
    .optional(),
  company_buy: z.boolean().optional(),
  company: z.string().optional(),
  ico: z.string().optional(),
  dic: z.string().optional(),
  ic_dph: z.string().optional(),
  image: z
    .object({
      id: z.number(),
    })
    .optional(),
});
